import React, { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import { Plus, Trash2, Pencil, Search, Printer } from 'lucide-react';
import { toast } from 'sonner';
import ExportButton from '../../components/ExportButton';
import PDFExportButton from '../../components/PDFExportButton';

// Generic, schema-driven page for any QSA log type.
// The schema is fetched from /api/logs/{logType}/schema and used to render
// the table and the add/edit modal automatically.

// Repeatable sub-rows editor (field.type === 'items'). Value is an array of
// objects keyed by the field's `columns`.
const ItemsField = ({ field, value, onChange }) => {
  const cols = field.columns || [];
  const rows = Array.isArray(value) ? value : [];
  const blank = () => cols.reduce((o, c) => ({ ...o, [c.name]: '' }), {});
  const display = rows.length ? rows : [blank()];
  const setRows = (next) => onChange(field.name, next);
  const updateRow = (idx, key, val) => {
    const next = display.map((r, i) => (i === idx ? { ...r, [key]: val } : r));
    setRows(next);
  };
  return (
    <div>
      <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
        <thead><tr>
          {cols.map((c) => <th key={c.name} style={{ padding: '4px', textAlign: 'left' }}>{c.label}</th>)}
          <th style={{ width: 32 }} />
        </tr></thead>
        <tbody>
          {display.map((row, idx) => (
            <tr key={idx}>
              {cols.map((c) => (
                <td key={c.name} style={{ padding: '3px' }}>
                  <input
                    type="text"
                    className="form-input"
                    style={{ fontSize: '12px', padding: '4px' }}
                    value={row[c.name] ?? ''}
                    onChange={(e) => updateRow(idx, c.name, e.target.value)}
                    data-testid={`log-items-${field.name}-${idx}-${c.name}`}
                  />
                </td>
              ))}
              <td style={{ padding: '3px' }}>
                {display.length > 1 && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRows(display.filter((_, i) => i !== idx))}>
                    <Trash2 size={12} color="#dc2626" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 6 }} onClick={() => setRows([...display, blank()])}>
        <Plus size={12} /> Add Row
      </button>
    </div>
  );
};

const inputForField = (field, value, onChange, dynamicOptions = {}) => {
  const common = {
    className: 'form-input',
    value: value ?? '',
    onChange: (e) => onChange(field.name, e.target.value),
    'data-testid': `log-field-${field.name}`,
    required: !!field.required,
  };
  switch (field.type) {
    case 'items':
      return <ItemsField field={field} value={value} onChange={onChange} />;
    case 'textarea':
      return <textarea {...common} rows={3} />;
    case 'date':
      return <input type="date" {...common} />;
    case 'time':
      return <input type="time" {...common} />;
    case 'number':
      return <input type="number" step="any" {...common} />;
    case 'boolean':
      return (
        <select {...common}>
          <option value="">--</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    case 'select': {
      // Options may be static (field.options) or sourced from another log's
      // entries (field.options_source, resolved into dynamicOptions).
      const opts = (field.options && field.options.length) ? field.options : (dynamicOptions[field.name] || []);
      return (
        <select {...common}>
          <option value="">-- Select --</option>
          {opts.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    default:
      return <input type="text" {...common} />;
  }
};

const renderCell = (field, value) => {
  if (field.type === 'items') {
    const n = Array.isArray(value) ? value.length : 0;
    return n ? `${n} item${n > 1 ? 's' : ''}` : '-';
  }
  if (value === null || value === undefined || value === '') return '-';
  if (field.type === 'boolean') return value ? 'Yes' : 'No';
  if (field.type === 'textarea') {
    const str = String(value);
    return str.length > 60 ? str.slice(0, 57) + '…' : str;
  }
  return String(value);
};

// Build a flat printable string for any field value.
const printValue = (field, value) => {
  if (value === null || value === undefined || value === '') return '';
  if (field.type === 'items') {
    const cols = field.columns || [];
    return (Array.isArray(value) ? value : [])
      .map((r) => cols.map((c) => r[c.name]).filter(Boolean).join(' - '))
      .join('; ');
  }
  if (field.type === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

// ── Per-entry controlled documents ───────────────────────────────────────────
// A log schema may declare `document_format`; the matching renderer below turns
// a single entry into the client's controlled form. Add a key here to support a
// new format — the per-row Print button appears automatically.
// "Indent for Lamination" (ANNEXURE-II, Format No. PD016-F02-00).
// The three signature columns are intentionally blank — they are signed by hand.
const MIN_INDENT_ROWS = 13;
const laminationIndentDoc = (schema, row) => {
  const materials = Array.isArray(row.materials) ? row.materials.filter(
    (m) => Object.values(m || {}).some((v) => String(v ?? '').trim())
  ) : [];
  const cell = (v) => `<td>${v ?? ''}</td>`;
  const bodyRows = [];
  for (let i = 0; i < Math.max(MIN_INDENT_ROWS, materials.length); i += 1) {
    const m = materials[i];
    bodyRows.push(`<tr>
      <td class="c">${m ? i + 1 : ''}</td>
      ${cell(m?.material_name)}${cell(m?.material_size)}${cell(m?.required_quantity)}
      <td></td>
      ${cell(m?.issued_quantity)}${cell(m?.issued_from_mrn)}
      <td></td><td></td>
    </tr>`);
  }
  return `<html><head><title>${schema.document_title} - ${row.batch_number || ''}</title>
    <style>
      @page{size:A4 landscape;margin:12mm}
      body{font-family:'Times New Roman',Times,serif;font-size:12px;color:#000;padding:4px}
      table{width:100%;border-collapse:collapse}
      td{border:1px solid #000;padding:6px 8px;font-size:12px;vertical-align:top}
      .head .brand{width:34%;text-align:center;font-weight:bold}
      .head .brand img{height:40px;width:auto;display:block;margin:0 auto 2px}
      .lbl{font-weight:bold}
      .sec td,.hdr td{text-align:center;font-weight:bold}
      .body td{height:26px}
      td.c{text-align:center}
      .foot{display:flex;justify-content:space-between;margin-top:14px;font-size:11px}
      @media print{button{display:none !important}}
    </style></head><body>
    <table class="head">
      <tr>
        <td class="brand" rowspan="2">
          <img src="https://hridya.dweb.co.in/hridayalogo.png" alt="Hridya Pack" />HRIDYA PACK
        </td>
        <td style="width:33%;text-align:center;font-weight:bold">${schema.document_annexure || ''}</td>
        <td style="width:33%"><span class="lbl">Effective Date:</span></td>
      </tr>
      <tr><td colspan="2"><span class="lbl">Title:</span> ${schema.document_title}</td></tr>
    </table>

    <table style="margin-top:-1px">
      <tr>
        <td colspan="5"><span class="lbl">Indent No.:</span> ${row.indent_no || ''}</td>
        <td colspan="4"><span class="lbl">Issued to Batch No.:</span> ${row.batch_number || ''}</td>
      </tr>
      <tr class="sec">
        <td colspan="5">Materials required for production</td>
        <td colspan="4">Material issuance details</td>
      </tr>
      <tr class="hdr">
        <td style="width:5%">S. No</td><td style="width:18%">Material Name</td><td style="width:10%">Material Size</td>
        <td style="width:10%">Required Quantity</td><td style="width:14%">Requested by (PD)<br/>Sign &amp; Date</td>
        <td style="width:10%">Issued Quantity</td><td style="width:11%">Issued from MRN No</td>
        <td style="width:11%">Issued by (WH)</td><td style="width:11%">Received by (PD)</td>
      </tr>
      <tbody class="body">${bodyRows.join('')}</tbody>
    </table>

    <div class="foot"><div>Format No.: ${schema.document_format_no || ''}</div><div>Page 1 of 1</div></div>
    <button onclick="window.print()" style="margin-top:16px;padding:8px 24px;cursor:pointer">Print</button>
    </body></html>`;
};

const DOCUMENT_RENDERERS = {
  lamination_indent: laminationIndentDoc,
};

const LogPage = ({ logType }) => {
  const [schema, setSchema] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [dynamicOptions, setDynamicOptions] = useState({});

  useEffect(() => {
    setSchema(null);
    setEntries([]);
    setEditingId(null);
    setFormData({});
    setDynamicOptions({});
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logType]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      // Schema gates the page; fetch it first. No toast on failure — the inline
      // "Unable to load this log" panel (with Retry) already surfaces the error.
      const sRes = await api.get(`/logs/${logType}/schema`);
      setSchema(sRes.data);
      // Entries are secondary: if they fail, still show the (empty) log table.
      try {
        const eRes = await api.get(`/logs/${logType}`);
        setEntries(Array.isArray(eRes.data) ? eRes.data : []);
      } catch (entriesErr) {
        console.error(entriesErr);
        setEntries([]);
      }
      // Resolve any select fields whose options come from another log's entries
      // (e.g. Visual Inspection Report inspector list ← Qualification register).
      try {
        const sourced = (sRes.data.fields || []).filter(f => f.options_source);
        if (sourced.length) {
          const results = await Promise.all(sourced.map(async (f) => {
            try {
              const r = await api.get(`/logs/${f.options_source.log_type}`);
              const vals = (Array.isArray(r.data) ? r.data : [])
                .map(row => row[f.options_source.field])
                .filter(v => v !== null && v !== undefined && String(v).trim() !== '');
              return [f.name, Array.from(new Set(vals.map(String)))];
            } catch { return [f.name, []]; }
          }));
          setDynamicOptions(Object.fromEntries(results));
        } else {
          setDynamicOptions({});
        }
      } catch { setDynamicOptions({}); }
    } catch (err) {
      console.error(err);
      setSchema(null);
    } finally {
      setLoading(false);
    }
  };

  const blankForm = useMemo(() => {
    if (!schema) return {};
    const out = {};
    schema.fields.forEach((f) => { out[f.name] = ''; });
    return out;
  }, [schema]);

  const openAdd = () => {
    setEditingId(null);
    setFormData(blankForm);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    const next = { ...blankForm };
    schema.fields.forEach((f) => {
      next[f.name] = row[f.name] ?? '';
    });
    setFormData(next);
    setShowModal(true);
  };

  const handleChange = (name, value) => {
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/logs/${logType}/${editingId}`, formData);
      } else {
        await api.post(`/logs/${logType}`, formData);
      }
      setShowModal(false);
      setEditingId(null);
      setFormData(blankForm);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await api.delete(`/logs/${logType}/${row.id}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // Print one entry as the controlled document its schema declares.
  const printDocument = (row) => {
    const render = DOCUMENT_RENDERERS[schema?.document_format];
    if (!render) return;
    const w = window.open('', `log_doc_${row.id}`, 'width=1000,height=760');
    w.document.open();
    w.document.write(render(schema, row));
    w.document.close();
  };

  const printLog = () => {
    if (!schema) return;
    const fields = schema.fields;
    const w = window.open('', `log_print_${logType}`, 'width=1000,height=760');
    w.document.open();

    if (schema.print_layout === 'record') {
      // One block per entry, as label/value pairs. Fields with no value are
      // omitted from the final document (per client requirement).
      const blocks = filtered.map((row) => {
        const lines = fields.map((f) => {
          const v = printValue(f, row[f.name]);
          if (v === '' || v === null || v === undefined) return '';
          return `<div class="row"><span class="k">${f.label}:</span><span class="v">${v}</span></div>`;
        }).filter(Boolean).join('');
        return `<div class="record">${lines}</div>`;
      }).join('');
      w.document.write(`<html><head><title>${schema.title}</title>
        <style>
          @page{size:A4;margin:14mm}
          body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:16px}
          h1{font-size:16px;margin:0 0 12px}
          .record{border:1px solid #888;border-radius:4px;padding:10px 12px;margin-bottom:12px;page-break-inside:avoid}
          .row{display:flex;gap:8px;margin:3px 0}
          .k{font-weight:bold;min-width:200px}
          @media print{button{display:none !important}}
        </style></head><body>
        <h1>${schema.title}</h1>
        ${blocks || '<p>No entries.</p>'}
        <button onclick="window.print()" style="margin-top:14px;padding:8px 24px;cursor:pointer">Print</button>
        </body></html>`);
      w.document.close();
      return;
    }

    const head = fields.map((f) => `<th>${f.label}</th>`).join('');
    const body = filtered.map((row) =>
      `<tr>${fields.map((f) => `<td>${printValue(f, row[f.name])}</td>`).join('')}</tr>`
    ).join('');
    w.document.write(`<html><head><title>${schema.title}</title>
      <style>
        @page{size:A4 landscape;margin:12mm}
        body{font-family:Arial,sans-serif;font-size:10px;color:#111;padding:14px}
        h1{font-size:16px;margin:0 0 10px}
        table{width:100%;border-collapse:collapse}
        th,td{border:1px solid #888;padding:4px 6px;text-align:left;font-size:9px}
        th{background:#1a237e;color:#fff}
        @media print{button{display:none !important}}
      </style></head><body>
      <h1>${schema.title}</h1>
      <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
      <button onclick="window.print()" style="margin-top:14px;padding:8px 24px;cursor:pointer">Print</button>
      </body></html>`);
    w.document.close();
  };

  const visibleFields = useMemo(() => (schema?.fields ?? []).slice(0, 7), [schema]);

  const filtered = useMemo(() => {
    if (!search.trim()) return entries;
    const q = search.toLowerCase();
    return entries.filter((row) =>
      Object.values(row).some((v) => String(v ?? '').toLowerCase().includes(q))
    );
  }, [entries, search]);

  if (loading && !schema) {
    return <div data-testid={`${logType}-page`} style={{ padding: 20 }}>Loading…</div>;
  }

  if (!schema) {
    const title = logType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return (
      <div data-testid={`${logType}-page`}>
        <div className="flex items-center justify-between mb-4">
          <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>{title}</h1>
        </div>
        <div className="card">
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-tertiary)' }} data-testid="log-no-data">
            No data found
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`${logType}-page`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>{schema.title}</h1>
          {schema.description && (
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>{schema.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={printLog} className="btn btn-secondary btn-sm" data-testid="log-print-btn">
            <Printer size={14} /> Print
          </button>
          <ExportButton endpoint={`/export/logs/${logType}`} filename={`${logType}_export.xlsx`} />
          <PDFExportButton endpoint={`/export/pdf/logs/${logType}`} filename={`${logType}_export.pdf`} />
          <button onClick={openAdd} className="btn btn-primary" data-testid="log-add-btn">
            <Plus size={16} /> Add Entry
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Search size={14} color="#888" />
          <input
            type="text"
            className="form-input"
            style={{ flex: 1, border: 'none', background: 'transparent', padding: 4 }}
            placeholder="Search across all columns…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="log-search"
          />
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{filtered.length} entries</span>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                {visibleFields.map((f) => <th key={f.name}>{f.label}</th>)}
                <th>Created</th>
                <th style={{ width: 150, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={visibleFields.length + 2} style={{ textAlign: 'center', padding: 24, color: 'var(--text-tertiary)' }} data-testid="log-no-data">
                    No data found
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id} data-testid={`log-row-${row.id}`}>
                    {visibleFields.map((f) => (
                      <td key={f.name}>{renderCell(f, row[f.name])}</td>
                    ))}
                    <td className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                      {row.created_by_name || '-'}<br/>
                      <span style={{ fontSize: 11 }}>{(row.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {DOCUMENT_RENDERERS[schema.document_format] && (
                        <button onClick={() => printDocument(row)} className="btn btn-ghost btn-sm" title={`Print ${schema.document_title}`} data-testid={`log-doc-print-${row.id}`}>
                          <Printer size={14} />
                        </button>
                      )}
                      <button onClick={() => openEdit(row)} className="btn btn-ghost btn-sm" title="Edit">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => handleDelete(row)} className="btn btn-ghost btn-sm" title="Delete">
                        <Trash2 size={14} color="#dc2626" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="log-modal" onClick={() => !saving && setShowModal(false)}>
          <div className="modal-content" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {editingId ? `Edit ${schema.title}` : `Add ${schema.title}`}
              </h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {schema.fields.map((field) => (
                  <div
                    key={field.name}
                    className="form-group"
                    style={{ gridColumn: (field.type === 'textarea' || field.type === 'items') ? '1 / -1' : 'auto', margin: 0 }}
                  >
                    <label className="form-label">
                      {field.label}{field.required ? ' *' : ''}
                    </label>
                    {inputForField(field, formData[field.name], handleChange, dynamicOptions)}
                    {field.help && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{field.help}</div>}
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button type="button" disabled={saving} onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" disabled={saving} className="btn btn-primary" data-testid="log-submit-btn">
                  {saving ? 'Saving…' : (editingId ? 'Update' : 'Add Entry')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogPage;
